import { useState } from 'react'
import { shield } from '@app/assets/images'
import { getPrivacyPolicyLink, getTermsOfUseLink } from '@app/utils/helper'

interface DisclaimerProps {
  onNext: () => void
  translate: (key: string) => string
}

const Disclaimer: React.FC<DisclaimerProps> = ({ onNext, translate }) => {
  const [accepted, setAccepted] = useState(false)

  const toggleAccepted = () => setAccepted(prev => !prev)

  return (
    <main className='min-h-screen flex flex-col items-center px-4 py-6 sm:py-10 bg-gray-100'> 
      <header className='flex flex-col items-center text-center mb-6 sm:mb-10'> 
        <div className='w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center rounded-lg bg-gray-200'> 
          <img src={shield} alt='Shield icon' className='w-7 h-7 sm:w-9 sm:h-9' /> 
        </div>
        <h1 className='text-xl sm:text-2xl font-semibold mt-3 sm:mt-4'> 
          {translate('risk_assessment.disclaimer.heading')}
        </h1>
      </header>

      <section className='w-full max-w-2xl flex flex-col flex-1 bg-white rounded-xl shadow-md border p-4 sm:p-6 mb-4 overflow-y-auto custom-scrollbar pr-1'>
        <p className='text-gray-800 text-sm leading-relaxed font-medium mb-4 sm:mb-6'> 
          {translate('risk_assessment.disclaimer.text')}
        </p>

        <div className='mt-auto space-y-4 sm:space-y-6 flex-shrink-0'>
          <nav className='flex space-x-2 sm:space-x-3 text-xs sm:text-sm font-semibold underline text-black-600 justify-center'>
            <a href={getTermsOfUseLink()} aria-label='View Terms of Use' target='_blank' rel='noopener noreferrer'>
              {translate('risk_assessment.disclaimer.terms_of_use')}
            </a>
            <span>|</span>
            <a href={getPrivacyPolicyLink()} target='_blank' rel='noopener noreferrer' aria-label='View Privacy Policy'>
              {translate('risk_assessment.disclaimer.privacy_policy')}
            </a>
          </nav>

          <div className='flex items-center space-x-2'>
            <input
              type='checkbox'
              id='agree'
              checked={accepted}
              onChange={toggleAccepted}
              className='h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0'
            />
            <label htmlFor='agree' className='text-xs sm:text-sm text-gray-700 leading-tight'>
              {translate('risk_assessment.disclaimer.agreement_label')}
            </label>
          </div>
        </div>
      </section>

      <footer className='w-full max-w-2xl px-4 pb-4'>
        <button
          type='button'
          disabled={!accepted}
          aria-disabled={!accepted}
          onClick={onNext}
          className={`w-full py-3 rounded-lg font-medium transition-colors duration-200 ${
            accepted
              ? 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-400 focus:outline-none'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {translate('risk_assessment.disclaimer.accept_button')}
        </button>
      </footer>
    </main>
  )
}

export default Disclaimer
